import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Building2, ChevronLeft, ChevronRight, FolderOpen, FileDown, Settings2,
  Plus, Pencil, Trash2, Loader2, X, Trash,
} from 'lucide-react';
import {
  AGENCIES, DEFAULT_AGENCY, loadPdfLibs,
  entryToWorker, workerHasHours, generatePurchaseOrderPdf,
} from './urenlijstPdf';
import WeekGrid from './WeekGrid';
import './PurchaseOrders.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const PROFIEL_KEYS = ['ozel', 'myform', 'entice'];

function fmtHours(n) {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace('.', ',');
}

// Maandag t/m zondag van een ISO-week, als dd-mm-jjjj.
function isoWeekRange(jaar, week) {
  const jan4 = new Date(jaar, 0, 4);
  const dow = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const f = (d) => `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  return { van: f(monday), tm: f(sunday) };
}

// ISO-weeknummer van vandaag — standaardwaarde bij het openen van een week.
function huidigeIsoWeek(d = new Date()) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  const jan1 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return { jaar: t.getUTCFullYear(), week: Math.ceil(((t - jan1) / 86400000 + 1) / 7) };
}

const EMPTY_BEDRIJF = {
  naam: '', adres: '', postcode: '', plaats: '', kvk: '', btw_nummer: '',
  contactpersoon: '', email: '',
  tarief_dag: '', tarief_overwerk: '', tarief_avondnacht: '',
  tarief_zaterdag: '', tarief_zondag: '', tarief_feestdag: '',
};

const RATE_LABELS = [
  ['tarief_dag', 'Dag'],
  ['tarief_overwerk', 'Overwerk'],
  ['tarief_avondnacht', 'Avond / nacht'],
  ['tarief_zaterdag', 'Zaterdag'],
  ['tarief_zondag', 'Zondag'],
  ['tarief_feestdag', 'Feestdag'],
];

export default function PurchaseOrders() {
  const [profiel, setProfiel] = useState(null);      // null = bedrijfskeuze (tussenstap)
  const [orders, setOrders] = useState([]);          // alle PO's van dit bureau
  const [loadingWeken, setLoadingWeken] = useState(false);
  const [sel, setSel] = useState(null);              // de geopende PO
  const [bedrijven, setBedrijven] = useState([]);
  const [bedrijfId, setBedrijfId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Nieuwe week openen (PO-weken staan los van de urenlijst en beginnen leeg)
  const nu = huidigeIsoWeek();
  const [newWeek, setNewWeek] = useState(nu.week);
  const [newYear, setNewYear] = useState(nu.jaar);

  // Beheerscherm voor PO-bedrijven
  const [manageOpen, setManageOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadBedrijven = useCallback(async () => {
    try {
      const res = await authFetch('/api/po/bedrijven');
      const d = await res.json();
      setBedrijven(Array.isArray(d) ? d : []);
    } catch { setBedrijven([]); }
  }, []);

  useEffect(() => { loadBedrijven(); }, [loadBedrijven]);

  const loadOrders = useCallback(async (key) => {
    setLoadingWeken(true);
    try {
      const res = await authFetch(`/api/po/orders?profiel=${encodeURIComponent(key)}`);
      const d = await res.json();
      const list = Array.isArray(d) ? d : [];
      setOrders(list);
      return list;
    } catch { setOrders([]); return []; }
    finally { setLoadingWeken(false); }
  }, []);

  const openProfiel = async (key) => {
    setProfiel(key);
    setSel(null);
    setBedrijfId('');
    setError('');
    const list = await loadOrders(key);
    if (list.length) selectOrder(list[0]);
  };

  // Een PO openen; het bedrijf dat eraan hangt komt meteen terug.
  const selectOrder = (o) => {
    setSel(o);
    setBedrijfId(o.bedrijf_id ? String(o.bedrijf_id) : '');
    setError('');
  };

  // Altijd een NIEUWE PO, ook als die week al open staat: dezelfde week kan
  // meerdere PO's hebben, bijvoorbeeld één per opdrachtgever.
  const nieuwePo = async (jaar, week) => {
    setError('');
    try {
      const res = await authFetch('/api/po/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profiel, jaar: Number(jaar), week: Number(week) }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Aanmaken mislukt'); }
      const created = await res.json();
      setOrders((prev) => [created, ...prev]
        .sort((a, b) => b.jaar - a.jaar || b.week - a.week || b.id - a.id));
      selectOrder(created);
    } catch (err) { setError(err.message || 'PO aanmaken mislukt.'); }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`PO van week ${o.week} — ${o.jaar}${o.bedrijf_naam ? ` (${o.bedrijf_naam})` : ''} verwijderen? De ingevoerde uren gaan weg.`)) return;
    try {
      const res = await authFetch(`/api/po/orders/${o.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      if (sel && sel.id === o.id) { setSel(null); setBedrijfId(''); }
    } catch { setError('PO verwijderen mislukt.'); }
  };

  // Het gekozen bedrijf blijft aan deze PO hangen, zodat twee PO's in dezelfde
  // week uit elkaar te houden zijn.
  const kiesBedrijf = async (value) => {
    setBedrijfId(value);
    setError('');
    if (!sel) return;
    const id = value ? Number(value) : null;
    setOrders((prev) => prev.map((o) => (o.id === sel.id
      ? { ...o, bedrijf_id: id, bedrijf_naam: bedrijven.find((b) => b.id === id)?.naam || null }
      : o)));
    setSel((prev) => (prev ? { ...prev, bedrijf_id: id } : prev));
    try {
      await authFetch(`/api/po/orders/${sel.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bedrijf_id: id }),
      });
    } catch { /* niet erg: de keuze staat al in beeld, de PDF werkt gewoon */ }
  };

  const agency = AGENCIES[profiel] || DEFAULT_AGENCY;
  const gridParams = useMemo(() => (sel ? { order_id: sel.id } : null), [sel]);
  const bedrijf = bedrijven.find((b) => String(b.id) === String(bedrijfId)) || null;

  // ── PDF genereren ──
  const downloadPo = async () => {
    if (!sel || !bedrijf || busy) return;
    setBusy(true);
    setError('');
    try {
      // Nog niet weggeschreven celwijzigingen eerst opslaan, anders mist de PDF ze.
      window.dispatchEvent(new Event('uren:flush'));
      await new Promise((r) => setTimeout(r, 350));
      await loadPdfLibs();
      const [workersRes, weekRes] = await Promise.all([
        authFetch('/api/workers').then((r) => { if (!r.ok) throw new Error('medewerkers'); return r.json(); }),
        authFetch(`/api/po/week?order_id=${sel.id}`)
          .then((r) => { if (!r.ok) throw new Error('week'); return r.json(); }),
      ]);
      const metaById = new Map((Array.isArray(workersRes) ? workersRes : []).map((w) => [w.id, w]));
      const entries = Array.isArray(weekRes?.entries) ? weekRes.entries : [];

      const list = [];
      for (const e of entries) {
        const meta = metaById.get(e.worker_id);
        if (!meta) continue;
        const w = entryToWorker(e, meta);
        if (!workerHasHours(w)) continue;
        list.push(w);
      }
      if (!list.length) { setError('Geen medewerkers met uren in deze week.'); return; }
      list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nl', { sensitivity: 'base' }));

      const { van, tm } = isoWeekRange(sel.jaar, sel.week);
      const doc = generatePurchaseOrderPdf(list, {
        weekNr: sel.week, jaar: sel.jaar, vanDate: van, tmDate: tm, agency, bedrijf,
      });
      doc.save(`Purchase Order ${bedrijf.naam} week ${sel.week} ${sel.jaar}.pdf`);
      loadOrders(profiel);
    } catch (err) {
      setError('PDF maken mislukt: ' + (err?.message || err));
    } finally {
      setBusy(false);
    }
  };

  // ── Bedrijven beheren ──
  const saveBedrijf = async (e) => {
    e.preventDefault();
    if (!form.naam.trim()) { setError('Bedrijfsnaam is verplicht.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await authFetch(
        form.id ? `/api/po/bedrijven/${form.id}` : '/api/po/bedrijven',
        { method: form.id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }
      );
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Opslaan mislukt'); }
      setForm(null);
      await loadBedrijven();
    } catch (err) {
      setError(err.message || 'Opslaan mislukt');
    } finally { setSaving(false); }
  };

  const removeBedrijf = async (b) => {
    if (!window.confirm(`Bedrijf "${b.naam}" verwijderen?`)) return;
    try {
      const res = await authFetch(`/api/po/bedrijven/${b.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      if (String(bedrijfId) === String(b.id)) setBedrijfId('');
      await loadBedrijven();
    } catch { setError('Verwijderen mislukt.'); }
  };

  // ── Tussenstap: kies het uitzendbureau ──
  if (!profiel) {
    return (
      <div className="po-root">
        <div className="po-head">
          <div>
            <h2>Purchase Orders</h2>
            <p>Kies een bedrijf om de weken te bekijken</p>
          </div>
          <button className="po-btn" onClick={() => setManageOpen(true)}>
            <Settings2 size={15} /> Bedrijven &amp; tarieven
          </button>
        </div>

        <div className="po-profielen">
          {PROFIEL_KEYS.map((k) => {
            const a = AGENCIES[k];
            return (
              <button key={k} className="po-profiel" onClick={() => openProfiel(k)}>
                <span className="po-profiel-icon"><Building2 size={22} /></span>
                <span className="po-profiel-naam">{a.name}</span>
                <span className="po-profiel-sub">{a.addr1}</span>
              </button>
            );
          })}
        </div>

        {manageOpen && (
          <BedrijvenBeheer
            bedrijven={bedrijven} form={form} setForm={setForm} saving={saving}
            onSave={saveBedrijf} onRemove={removeBedrijf} onClose={() => { setManageOpen(false); setForm(null); }}
            error={error}
          />
        )}
      </div>
    );
  }

  // ── Weken + PO maken ──
  return (
    <div className="po-root">
      <div className="po-head">
        <div className="po-crumbs">
          <button className="po-back" onClick={() => { setProfiel(null); setSel(null); }}>
            <ChevronLeft size={15} /> Bedrijven
          </button>
          <span className="po-crumb-sep">/</span>
          <span className="po-crumb-now">{agency.name}</span>
        </div>
        <button className="po-btn" onClick={() => setManageOpen(true)}>
          <Settings2 size={15} /> Bedrijven &amp; tarieven
        </button>
      </div>

      <div className="po-workspace">
        <aside className="po-weeks">
          <div className="po-newweek">
            <div className="po-newweek-inputs">
              <label>Week
                <input type="number" min="1" max="53" value={newWeek}
                  onChange={(e) => setNewWeek(Math.max(1, Math.min(53, parseInt(e.target.value, 10) || 1)))} />
              </label>
              <label>Jaar
                <input type="number" min="2020" max="2099" value={newYear}
                  onChange={(e) => setNewYear(parseInt(e.target.value, 10) || nu.jaar)} />
              </label>
            </div>
            <button className="po-btn po-btn-primary po-newweek-btn" onClick={() => nieuwePo(newYear, newWeek)}>
              <Plus size={15} /> Week openen
            </button>
            <span className="po-newweek-hint">Maakt elke keer een nieuwe PO — dezelfde week mag meerdere keren.</span>
          </div>

          <div className="po-weeks-label">Purchase Orders</div>
          {loadingWeken && <div className="po-empty-sm">Laden…</div>}
          {!loadingWeken && orders.length === 0 && (
            <div className="po-empty-sm"><FolderOpen size={18} /><span>Nog geen PO's</span></div>
          )}
          {orders.map((o) => {
            const active = sel && sel.id === o.id;
            return (
              <div key={o.id} className={`po-week ${active ? 'active' : ''}`}>
                <button className="po-week-open" onClick={() => selectOrder(o)}>
                  <FolderOpen size={15} className="po-week-icon" />
                  <span className="po-week-main">
                    <span className="po-week-title">Week {o.week} · {o.jaar}</span>
                    <span className="po-week-bedrijf">{o.bedrijf_naam || 'Geen bedrijf gekozen'}</span>
                    <span className="po-week-meta">{o.aantalPersonen} pers · {fmtHours(o.totaalUren)}u</span>
                  </span>
                  <ChevronRight size={14} className="po-week-arrow" />
                </button>
                <button className="po-week-del" title="PO verwijderen" onClick={() => removeOrder(o)}>
                  <Trash size={13} />
                </button>
              </div>
            );
          })}
        </aside>

        <div className="po-main">
          {!sel ? (
            <div className="po-empty"><FolderOpen size={34} strokeWidth={1.2} />
              <p>Kies links een PO, of open een nieuwe week.</p></div>
          ) : (
            <>
              <div className="po-bar">
                <span className="po-bar-week">Week {sel.week} — {sel.jaar}</span>

                <label className="po-bar-field">
                  <span>Bedrijf selecteren</span>
                  <select value={bedrijfId} onChange={(e) => kiesBedrijf(e.target.value)}>
                    <option value="">— Kies bedrijf —</option>
                    {bedrijven.map((b) => <option key={b.id} value={b.id}>{b.naam}</option>)}
                  </select>
                </label>

                <button className="po-btn po-btn-primary" onClick={downloadPo} disabled={!bedrijf || busy}>
                  {busy ? <Loader2 size={15} className="po-spin" /> : <FileDown size={15} />}
                  {busy ? 'Bezig…' : 'PDF download'}
                </button>
              </div>

              {bedrijf && (
                <div className="po-rates">
                  <span className="po-rates-label">Tarieven van {bedrijf.naam}:</span>
                  {RATE_LABELS.map(([k, label]) => (
                    <span key={k} className="po-rate">
                      {label} <strong>{Number(bedrijf[k]) > 0 ? `€ ${Number(bedrijf[k])}` : '—'}</strong>
                    </span>
                  ))}
                </div>
              )}

              {!bedrijf && (
                <p className="po-hint">Kies een bedrijf; de tarieven van dat bedrijf worden op de PO gebruikt.</p>
              )}
              {error && <div className="po-error">{error}</div>}

              <div className="po-grid">
                <WeekGrid
                  key={`po-${sel.id}`}
                  apiBase="/api/po"
                  extraParams={gridParams}
                  profiel={profiel} jaar={sel.jaar} week={sel.week}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {manageOpen && (
        <BedrijvenBeheer
          bedrijven={bedrijven} form={form} setForm={setForm} saving={saving}
          onSave={saveBedrijf} onRemove={removeBedrijf} onClose={() => { setManageOpen(false); setForm(null); }}
          error={error}
        />
      )}
    </div>
  );
}

// ── Beheerscherm: bedrijven met hun tarieven ──────────────────────────────
function BedrijvenBeheer({ bedrijven, form, setForm, saving, onSave, onRemove, onClose, error }) {
  return (
    <div className="po-modal-backdrop" onMouseDown={onClose}>
      <div className="po-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="po-modal-head">
          <span>Bedrijven &amp; tarieven</span>
          <button className="po-modal-x" onClick={onClose}><X size={16} /></button>
        </div>

        {error && <div className="po-error">{error}</div>}

        {!form ? (
          <>
            <button className="po-btn po-btn-primary po-add" onClick={() => setForm({ ...EMPTY_BEDRIJF })}>
              <Plus size={15} /> Bedrijf toevoegen
            </button>

            {bedrijven.length === 0 ? (
              <p className="po-empty-sm">Nog geen bedrijven. Voeg er één toe met de eigen tarieven.</p>
            ) : (
              <ul className="po-list">
                {bedrijven.map((b) => (
                  <li key={b.id} className="po-list-row">
                    <div className="po-list-main">
                      <strong>{b.naam}</strong>
                      <span className="po-list-sub">
                        {[b.postcode, b.plaats].filter(Boolean).join(' ') || '—'}
                        {' · '}
                        {RATE_LABELS.map(([k, l]) => `${l} €${Number(b[k]) > 0 ? Number(b[k]) : 0}`).join(' · ')}
                      </span>
                    </div>
                    <div className="po-list-actions">
                      <button className="po-icon" title="Bewerken"
                        onClick={() => setForm({
                          id: b.id,
                          ...Object.fromEntries(Object.keys(EMPTY_BEDRIJF).map((k) => [k, b[k] ?? ''])),
                        })}>
                        <Pencil size={13} />
                      </button>
                      <button className="po-icon po-icon-del" title="Verwijderen" onClick={() => onRemove(b)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <form className="po-form" onSubmit={onSave}>
            <div className="po-form-grid">
              <label className="po-field po-span2">
                <span>Bedrijfsnaam <b>*</b></span>
                <input value={form.naam} autoFocus
                  onChange={(e) => setForm((f) => ({ ...f, naam: e.target.value }))} />
              </label>
              <label className="po-field po-span2">
                <span>Adres</span>
                <input value={form.adres} onChange={(e) => setForm((f) => ({ ...f, adres: e.target.value }))} />
              </label>
              <label className="po-field">
                <span>Postcode</span>
                <input value={form.postcode} onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value }))} />
              </label>
              <label className="po-field">
                <span>Plaats</span>
                <input value={form.plaats} onChange={(e) => setForm((f) => ({ ...f, plaats: e.target.value }))} />
              </label>
              <label className="po-field">
                <span>KvK</span>
                <input value={form.kvk} onChange={(e) => setForm((f) => ({ ...f, kvk: e.target.value }))} />
              </label>
              <label className="po-field">
                <span>BTW-nummer</span>
                <input value={form.btw_nummer} onChange={(e) => setForm((f) => ({ ...f, btw_nummer: e.target.value }))} />
              </label>
            </div>

            <div className="po-form-sub">Tarieven per uur — gelden voor iedereen op de PO van dit bedrijf</div>
            <div className="po-form-grid po-form-rates">
              {RATE_LABELS.map(([k, label]) => (
                <label className="po-field" key={k}>
                  <span>{label}</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00"
                    value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
                </label>
              ))}
            </div>

            <div className="po-form-actions">
              <button type="button" className="po-btn" onClick={() => setForm(null)} disabled={saving}>Annuleren</button>
              <button type="submit" className="po-btn po-btn-primary" disabled={saving}>
                {saving ? <Loader2 size={15} className="po-spin" /> : null}
                {form.id ? 'Opslaan' : 'Toevoegen'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
